/**
 * The encounter commit record — `RosterState.encounterCommits`, when each encounter was last
 * committed back to the roster.
 *
 * A leaf-handler group of `./leafReducer`'s switch; see `../reducer` for the tier's contract.
 */

import { ok, type CommandError, type Result } from "@lancer-vtt/shared";
import type { RecordEncounterCommitCommand } from "../commands";
import type { RosterState } from "../types";
import type { RosterApplyResult } from "./context";

/**
 * Set `encounterCommits[encounterId]` to `at`, or remove it when `at` is `null`. The field is
 * dropped entirely once its last entry goes, so undoing a first record restores the exact
 * prior state. Names no pilot, so it emits a `roster_ledger_changed` event rather than
 * `roster_changed`.
 */
export function applyRecordEncounterCommit(
  state: RosterState,
  cmd: RecordEncounterCommitCommand,
): Result<RosterApplyResult, CommandError> {
  const prior = state.encounterCommits?.[cmd.encounterId];
  const { [cmd.encounterId]: _removed, ...rest } = state.encounterCommits ?? {};
  const next = cmd.at === null ? rest : { ...rest, [cmd.encounterId]: cmd.at };
  const { encounterCommits: _old, ...base } = state;
  return ok({
    state: Object.keys(next).length === 0 ? base : { ...base, encounterCommits: next },
    inverse: { type: "record_encounter_commit", actorId: cmd.actorId, encounterId: cmd.encounterId, at: prior ?? null },
    events: [{ type: "roster_ledger_changed", ledger: "encounterCommits", key: cmd.encounterId }],
  });
}
